import { persistedSettingsSeed, SETTINGS_STORAGE_KEY } from '../engine/config.js';

const settingsUi = {
  visible: false,
  controlCaptureTarget: null
};

function normalizeArrowKeyAlias(raw) {
  if (raw === 'Left') return 'ArrowLeft';
  if (raw === 'Right') return 'ArrowRight';
  if (raw === 'Up') return 'ArrowUp';
  if (raw === 'Down') return 'ArrowDown';
  return raw;
}

const KEY_LABEL_BY_CODE = new Map([
  ['ArrowLeft', 'LEFT'],
  ['ArrowRight', 'RIGHT'],
  ['ArrowUp', 'UP'],
  ['ArrowDown', 'DOWN'],
  ['Space', 'SPACE'],
  ['Enter', 'ENTER'],
  ['Tab', 'TAB'],
  ['Backspace', 'BACKSPACE'],
  ['Delete', 'DELETE']
]);

const NON_BINDABLE_CONTROL_KEYS = new Set([
  'Escape',
  'Shift',
  'Control',
  'Alt',
  'Meta',
  'CapsLock'
]);

const DEFAULT_TURN_BINDINGS = {
  left: { code: 'ArrowLeft', key: 'ArrowLeft' },
  right: { code: 'ArrowRight', key: 'ArrowRight' }
};

function defaultTurnBindingFor(side) {
  const fallback = side === 'left' ? DEFAULT_TURN_BINDINGS.left : DEFAULT_TURN_BINDINGS.right;
  return { code: fallback.code, key: fallback.key };
}

function normalizeTurnBinding(raw, fallback) {
  const fallbackBinding = fallback && typeof fallback === 'object' ? fallback : { code: '', key: '' };
  let code = '';
  let key = '';
  if (raw && typeof raw === 'object') {
    code = String(raw.code || '').trim();
    key = String(raw.key || '').trim();
  } else if (typeof raw === 'string') {
    key = String(raw).trim();
  }
  key = normalizeArrowKeyAlias(key);
  if (!code && !key) {
    return { code: String(fallbackBinding.code || ''), key: String(fallbackBinding.key || '') };
  }
  return { code, key };
}

function keyLabelFromCode(code) {
  const normalized = String(code || '').trim();
  if (!normalized) return '';
  const direct = KEY_LABEL_BY_CODE.get(normalized);
  if (direct) return direct;
  const keyMatch = normalized.match(/^Key([A-Z])$/);
  if (keyMatch) return keyMatch[1];
  const digitMatch = normalized.match(/^Digit([0-9])$/);
  if (digitMatch) return digitMatch[1];
  const numpadMatch = normalized.match(/^Numpad([0-9])$/);
  if (numpadMatch) return `NUM ${numpadMatch[1]}`;
  return '';
}

function keyLabelFromKey(key) {
  const normalized = normalizeArrowKeyAlias(String(key || '').trim());
  if (!normalized) return '';
  const arrowLabel = KEY_LABEL_BY_CODE.get(normalized);
  if (arrowLabel) return arrowLabel;
  if (normalized === ' ') return 'SPACE';
  if (normalized.length === 1) return normalized.toUpperCase();
  return normalized.toUpperCase();
}

function turnBindingLabel(binding, fallbackLabel) {
  const fromCode = keyLabelFromCode(binding?.code);
  if (fromCode) return fromCode;
  const fromKey = keyLabelFromKey(binding?.key);
  if (fromKey) return fromKey;
  return fallbackLabel;
}

function keyBindingMatches(binding, input) {
  const expectedCode = String(binding?.code || '').trim();
  const expectedKey = normalizeArrowKeyAlias(String(binding?.key || '').trim());
  if (!expectedCode && !expectedKey) return false;

  if (typeof input === 'string') {
    const value = normalizeArrowKeyAlias(input.trim());
    if (!value) return false;
    if (expectedCode && expectedCode === value) return true;
    if (expectedKey && expectedKey.toLowerCase() === value.toLowerCase()) return true;
    return false;
  }

  const inputCode = String(input?.code || '').trim();
  const inputKey = normalizeArrowKeyAlias(String(input?.key || '').trim());
  if (expectedCode && inputCode && expectedCode === inputCode) return true;
  if (expectedKey && inputKey && expectedKey.toLowerCase() === inputKey.toLowerCase()) return true;
  return false;
}

function isBindableControlKeyEvent(event) {
  if (!event || event.repeat) return false;
  const key = String(event.key || '').trim();
  if (!key) return false;
  return !NON_BINDABLE_CONTROL_KEYS.has(key);
}

function turnBindingFromEvent(event) {
  const code = String(event?.code || '').trim();
  const key = normalizeArrowKeyAlias(String(event?.key || '').trim());
  if (!code && !key) return null;
  return { code, key };
}

function setControlCaptureTarget(target) {
  if (target === 'left' || target === 'right') {
    settingsUi.controlCaptureTarget = target;
  } else {
    settingsUi.controlCaptureTarget = null;
  }
}

const settings = {
  turnLeftKey: normalizeTurnBinding(persistedSettingsSeed.turnLeftKey, defaultTurnBindingFor('left')),
  turnRightKey: normalizeTurnBinding(persistedSettingsSeed.turnRightKey, defaultTurnBindingFor('right')),
  soundVolume: (() => {
    const raw = Number(persistedSettingsSeed.soundVolume);
    if (!Number.isFinite(raw)) return 50;
    return Math.max(0, Math.min(100, Math.round(raw)));
  })(),
  // No longer user-configurable (the settings UI keeps only controls and
  // sound): pinned to the tuned defaults so stale persisted values cannot
  // silently degrade input feel or rendering.
  resolutionPercent: 100,
  // 50 is the ceiling inputLagReductionMs() clamps to. Both the server clamp
  // (applyServerMs >= lastSimTickEndMs) and the client's own anchor clamp in
  // resolveInputApplyServerMs saturate well below this, so 45 and 50 were
  // measured to give byte-identical predicted poses; this pins the knob at the
  // top of its range rather than mid-range.
  inputLagReductionMs: 50,
  // Event queue compensation is now capped at 34 ms, so Chrome/Edge can use
  // their high-resolution key timestamp without the old unbounded backdate
  // making delayed turns feel heavy.
  useKeyPressTimestamp: true,
  powerupSpawnSounds: typeof persistedSettingsSeed.powerupSpawnSounds === 'boolean'
    ? persistedSettingsSeed.powerupSpawnSounds
    : true
};

function persistSettings() {
  try {
    window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (_) {
    // ignore persistence failures
  }
}

function keyMatchesTurnLeft(input) {
  return keyBindingMatches(settings.turnLeftKey, input);
}

function keyMatchesTurnRight(input) {
  return keyBindingMatches(settings.turnRightKey, input);
}

// normalizeArrowKeyAlias, defaultTurnBindingFor and normalizeTurnBinding stay
// private: nothing outside this file used them, and they are only meaningful
// alongside the seeding above.
export {
  settingsUi,
  settings,
  turnBindingLabel,
  keyBindingMatches,
  isBindableControlKeyEvent,
  turnBindingFromEvent,
  setControlCaptureTarget,
  persistSettings,
  keyMatchesTurnLeft,
  keyMatchesTurnRight
};
